import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Button,
  InputAdornment,
} from "@mui/material";

/** what an empty field shows (not "לא ידוע" - an empty value is just not entered yet) */
const EMPTY_VALUE = "-";

export interface EditableField {
  field: string;
  headerName: string;
  value: string;
  type?: "text" | "email" | "number" | "date" | "select";
  required?: boolean;
  editable?: boolean;
  options?: { label: string; value: string }[];
  valueFormatter?: (value: string) => string;
  /** פורמט הערך בתוך שדה העריכה (למשל הוספת פסיקים לסכום כסף) */
  inputFormatter?: (value: string) => string;
  /** המר ערך מוצג חזרה לגולמי לפני שמירה (למשל הסרת פסיקים) */
  inputParser?: (value: string) => string;
}

interface EditableFieldsBlockProps {
  fields: EditableField[];
  onChange: (field: string, value: string) => void;
  title?: string;
  initialEditMode?: boolean;
  onEditModeChange?: (editMode: boolean) => void;
  noPadding?: boolean;
}

export const EditableFieldsBlock: React.FC<EditableFieldsBlockProps> = ({
  fields,
  onChange,
  title,
  initialEditMode = false,
  onEditModeChange,
  noPadding = false,
}) => {
  const [editMode, setEditMode] = useState(initialEditMode);
  const [localValues, setLocalValues] = useState<Record<string, string>>(
    Object.fromEntries(fields.map((f) => [f.field, f.value]))
  );

  // Update local values when a field's value prop actually changes (e.g., after save).
  // The parent usually builds `fields` anew on every render - resetting on the array identity would
  // wipe what the user just chose whenever the parent re-renders (e.g. while the save is pending).
  const fieldValuesKey = JSON.stringify(fields.map((f) => [f.field, f.value]));
  const previousValues = useRef<Record<string, string>>(Object.fromEntries(fields.map((f) => [f.field, f.value])));
  useEffect(() => {
    const next = Object.fromEntries(fields.map((f) => [f.field, f.value]));
    const changed = Object.keys(next).filter((key) => next[key] !== previousValues.current[key]);
    previousValues.current = next;
    if (changed.length === 0) return;
    setLocalValues((prev) => ({ ...prev, ...Object.fromEntries(changed.map((key) => [key, next[key]])) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValuesKey]);

  // Notify parent when edit mode changes
  useEffect(() => {
    onEditModeChange?.(editMode);
  }, [editMode, onEditModeChange]);

  const getRawValue = (fieldKey: string, displayValue: string): string => {
    const fieldConfig = fields.find((f) => f.field === fieldKey);
    return fieldConfig?.inputParser ? fieldConfig.inputParser(displayValue) : displayValue;
  };

  const handleLocalChange = (fieldKey: string, displayValue: string) => {
    const raw = getRawValue(fieldKey, displayValue);
    setLocalValues((prev) => ({ ...prev, [fieldKey]: raw }));
  };

  /** saves a field's value - only when it differs from the saved one */
  const commit = (field: string, value: string) => {
    const fieldConfig = fields.find((f) => f.field === field);
    if (!fieldConfig || value === fieldConfig.value) return;

    if (fieldConfig.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) return;
    }

    onChange(field, value);
  };

  const handleBlur = (field: string) => commit(field, localValues[field]);

  // a select is saved on choosing - its blur fires when the menu opens, before anything was chosen
  const handleSelectChange = (field: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [field]: value }));
    commit(field, value);
  };

  const handleSaveAndClose = () => {
    // Save all changed fields before closing edit mode
    fields.forEach((field) => {
      const currentValue = localValues[field.field];
      const originalValue = field.value;

      // Only save if value changed and field is editable
      if (currentValue !== originalValue && field.editable !== false) {
        // Validate email if needed
        if (field.type === "email") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(currentValue)) return;
        }

        onChange(field.field, currentValue);
      }
    });

    setEditMode(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: noPadding ? 0 : 2,
        backgroundColor: noPadding ? "transparent" : "background.default",
        borderRadius: noPadding ? 0 : 2,
        boxShadow: noPadding ? 0 : 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        {title && <Typography variant="h6">{title}</Typography>}
        <Button
          size="small"
          onClick={() => editMode ? handleSaveAndClose() : setEditMode(true)}
        >
          {/* changes are saved as each field is left / chosen - this only leaves edit mode */}
          {editMode ? "סיימתי לערוך" : "ערוך"}
        </Button>
      </Box>

      <Grid container spacing={0.5}>
        {fields.map((field) => (
          <Grid item xs={12} sm={6} key={field.field}>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="body2" color="text.secondary">
                {field.headerName}
              </Typography>
              {editMode && field.editable !== false ? (
                field.type === "select" ? (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={localValues[field.field] || ""}
                    onChange={(e) => handleSelectChange(field.field, e.target.value)}
                    inputProps={{ dir: "rtl" }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { direction: "rtl" } } } }}
                  >
                    {field.options?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    type={field.inputFormatter ? "text" : field.type === "email" ? "email" : field.type || "text"}
                    fullWidth
                    size="small"
                    required={field.required}
                    value={field.inputFormatter
                      ? field.inputFormatter(localValues[field.field] || "")
                      : localValues[field.field] || ""}
                    onChange={(e) => handleLocalChange(field.field, e.target.value)}
                    onBlur={() => handleBlur(field.field)}
                    InputProps={
                      field.type === "email"
                        ? { startAdornment: <InputAdornment position="start">@</InputAdornment> }
                        : undefined
                    }
                  />
                )
              ) : (
                <Typography variant="body1">
                  {(field.valueFormatter
                    ? field.valueFormatter(localValues[field.field] || "")
                    : localValues[field.field]) || EMPTY_VALUE}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
